import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Calendar as CalendarIcon, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import TreeLoadingSpinner from '@/components/TreeLoadingSpinner';

const CalendarPage = () => {
  const navigate = useNavigate();
  const [devotionals, setDevotionals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDevotionals();
  }, []);

  const loadDevotionals = async () => {
    try {
      const response = await axios.get(`${API}/devotionals`, { withCredentials: true });
      setDevotionals(response.data);
    } catch (error) {
      toast.error('Failed to load devotionals');
    } finally {
      setLoading(false);
    }
  };

  const getSeasonColor = (season) => {
    const colors = {
      'Lent': 'bg-purple-100 text-purple-900',
      'Advent': 'bg-purple-100 text-purple-900',
      'Easter': 'bg-yellow-100 text-yellow-900',
      'Ordinary Time': 'bg-green-100 text-green-900',
      'Pentecost': 'bg-red-100 text-red-900'
    };
    return colors[season] || 'bg-gray-100 text-gray-900';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <TreeLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="calendar-title">
            Devotional Calendar
          </h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-today-btn">
            Back to Today
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="calendar-grid">
          {devotionals.map((dev) => (
            <Card
              key={dev.devotional_id}
              data-testid={`devotional-card-${dev.day_number}`}
              className="p-6 cursor-pointer hover:shadow-md transition-shadow border border-primary/10"
              onClick={() => {
                // Navigate to dashboard with specific devotional
                navigate(`/dashboard?day=${dev.day_number}`);
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="text-sm text-muted-foreground">Day {dev.day_number}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getSeasonColor(dev.season)}`}>
                  {dev.season}
                </span>
              </div>
              
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {dev.liturgical_day}
              </h3>
              <p className="text-sm text-muted-foreground">{dev.date}</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;